/**
 * RiderFlagsBadge · v2.8+E319
 * ───────────────────────────
 * Prominent, impossible-to-miss RED rider-flag indicator used on the Ride
 * Coordinator cards (admin) and the driver ride cards. Renders NOTHING when
 * there are no flags.
 *
 * modes:
 *   • "chip"   — icon + count only (compact card; full list lives elsewhere).
 *   • "list"   — icon + count + always-open list (detail modal).
 *   • "expand" — icon + count + chevron; the list toggles on tap (driver card).
 */
import { useState } from "react";
import { Flag, TriangleAlert, ChevronDown, ChevronUp } from "lucide-react";
import { isCriticalFlag } from "@/lib/riderFlags";

export default function RiderFlagsBadge({ flags, testId = "rider-flags", mode = "expand" }) {
  const list = (Array.isArray(flags) ? flags : []).filter(Boolean);
  const [open, setOpen] = useState(false);
  if (list.length === 0) return null;
  // v2.8+E320 · Severity — a critical flag (Banned Rider / Possible Violent)
  // gives the whole badge + its chip a stronger, pulsing red treatment.
  const critical = list.some(isCriticalFlag);

  const clickable = mode === "expand";
  const showList = mode === "list" || (mode === "expand" && open);
  const toggle = (e) => {
    if (!clickable) return;
    e.stopPropagation();
    setOpen((o) => !o);
  };

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={toggle}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(e); } } : undefined}
      data-testid={testId}
      data-open={showList ? "true" : "false"}
      className={`rounded-lg border-2 px-2.5 py-1.5 ${critical ? "border-red-500 bg-red-600/20 ring-1 ring-red-400/70" : "border-red-500 bg-red-500/15"} ${clickable ? "cursor-pointer hover:bg-red-500/25" : ""}`}
    >
      <div className="flex items-center gap-1.5">
        <TriangleAlert className={`h-4 w-4 text-red-400 flex-shrink-0 ${critical ? "animate-pulse" : ""}`} />
        <span className={`text-[10px] uppercase tracking-widest font-black ${critical ? "text-red-200" : "text-red-300"}`}>
          {list.length} Rider Flag{list.length > 1 ? "s" : ""}
        </span>
        {critical && (
          <span className="text-[8px] uppercase tracking-widest font-black text-white bg-red-600 px-1.5 py-0.5 rounded animate-pulse" data-testid={`${testId}-critical`}>Critical</span>
        )}
        {mode === "chip" && (
          <span className="text-[9px] text-red-300/80 normal-case tracking-normal font-semibold">— open card for details</span>
        )}
        {clickable && (
          <span className="ml-auto text-red-300/80">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        )}
      </div>
      {showList && (
        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`${testId}-list`}>
          {list.map((f, i) => {
            const crit = isCriticalFlag(f);
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${crit ? "bg-red-600 border border-red-300 text-white font-black animate-pulse" : "bg-red-500/25 border border-red-500/60 text-red-100"}`}
                data-testid={`${testId}-item`}
              >
                <Flag className="h-2.5 w-2.5" />{f}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
